import { useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Badge, Button } from '@lineageatlas/design-system';
import { useApi, apiFetch } from '../hooks/useApi';
import type { CellLine } from '@lineageatlas/shared';
import type { BadgeStatus } from '@lineageatlas/design-system';

// ─── Types ────────────────────────────────────────────────────────────────────

interface ColumnMapping {
  column_index: number;
  column_header: string;
  mapped_to: string;
  confidence: number;
  unit_detected: string;
  notes: string;
}

interface AnalyzeResponse {
  headers: string[];
  sample_rows: (string | number)[][];
  mappings: ColumnMapping[];
  lot_column: number | null;
  date_column: number | null;
  passage_column: number | null;
  warnings: string[];
  total_rows: number;
  clinic_id: string;
}

interface ImportedEntry {
  id: string;
  overall_result: string;
}

interface ExecuteResponse {
  imported_count: number;
  lot_id: string;
  entries: ImportedEntry[];
}

type Step = 'upload' | 'mapping' | 'results';

// ─── Constants ────────────────────────────────────────────────────────────────

const KNOWN_MARKERS = [
  'CD73',
  'CD90',
  'CD105',
  'CD34',
  'CD45',
  'CD11b',
  'CD14',
  'CD19',
  'CD79a',
  'HLA-DR',
  'Viability',
  '__skip__',
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

function confidenceVariant(confidence: number): BadgeStatus {
  if (confidence >= 0.9) return 'excellent';
  if (confidence >= 0.7) return 'good';
  return 'risk';
}

function confidenceLabel(confidence: number): string {
  if (confidence >= 0.9) return `${Math.round(confidence * 100)}%`;
  if (confidence >= 0.7) return `${Math.round(confidence * 100)}%`;
  return `${Math.round(confidence * 100)}%`;
}

function resultVariant(result: string): BadgeStatus {
  switch (result) {
    case 'pass':
      return 'excellent';
    case 'borderline':
      return 'caution';
    case 'fail':
      return 'risk';
    default:
      return 'good';
  }
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// ─── Select style ─────────────────────────────────────────────────────────────

const SELECT_STYLE: React.CSSProperties = {
  height: '32px',
  padding: '0 var(--space-3)',
  borderRadius: 'var(--radius-md)',
  border: '1px solid var(--color-border)',
  background: 'var(--color-bg-input, var(--color-bg-surface))',
  color: 'var(--color-text)',
  fontSize: 'var(--text-caption)',
  fontFamily: 'var(--font-body)',
  cursor: 'pointer',
  minWidth: '160px',
};

// ─── Upload Icon ──────────────────────────────────────────────────────────────

function UploadIcon() {
  return (
    <svg
      width="40"
      height="40"
      viewBox="0 0 40 40"
      fill="none"
      aria-hidden="true"
      style={{ flexShrink: 0 }}
    >
      <rect
        x="4"
        y="4"
        width="32"
        height="32"
        rx="6"
        stroke="var(--color-border-bright)"
        strokeWidth="1.5"
        strokeDasharray="4 3"
      />
      <path
        d="M20 26V14M14 20l6-6 6 6"
        stroke="var(--color-accent-primary)"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// ─── Step 1: Upload ───────────────────────────────────────────────────────────

function UploadStep({ onAnalyzed }: { onAnalyzed: (result: AnalyzeResponse, file: File) => void }) {
  const [file, setFile] = useState<File | null>(null);
  const [dragging, setDragging] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFile = (f: File) => {
    const allowed = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
      'text/csv',
      'application/csv',
    ];
    const extOk = /\.(xlsx|xls|csv)$/i.test(f.name);
    if (!allowed.includes(f.type) && !extOk) {
      setError('Only .xlsx, .xls, and .csv files are supported.');
      return;
    }
    setFile(f);
    setError(null);
  };

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragging(false);
      const dropped = e.dataTransfer.files[0];
      if (dropped) handleFile(dropped);
    },
    [],
  );

  const handleAnalyze = async () => {
    if (!file) return;
    setAnalyzing(true);
    setError(null);

    try {
      const form = new FormData();
      form.append('file', file);

      const TOKEN_KEY = 'lineageatlas_token';
      const token = localStorage.getItem(TOKEN_KEY);
      const headers: Record<string, string> = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const res = await fetch('/api/v1/import/analyze', {
        method: 'POST',
        headers,
        body: form,
      });

      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error ?? `Upload failed: ${res.status}`);
      }

      const json = (await res.json()) as { data: AnalyzeResponse };
      onAnalyzed(json.data, file);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Analysis failed. Please try again.');
    } finally {
      setAnalyzing(false);
    }
  };

  return (
    <div>
      {/* Drop zone */}
      <div
        role="button"
        tabIndex={0}
        aria-label="Upload spreadsheet — drag and drop or click to browse"
        onClick={() => fileInputRef.current?.click()}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            fileInputRef.current?.click();
          }
        }}
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        style={{
          border: `2px dashed ${dragging ? 'var(--color-accent-primary)' : 'var(--color-border-bright)'}`,
          borderRadius: 'var(--radius-lg)',
          padding: 'var(--space-12) var(--space-6)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 'var(--space-4)',
          cursor: 'pointer',
          transition: 'border-color var(--duration-normal), background var(--duration-normal)',
          background: dragging ? 'var(--color-accent-primary-subtle)' : 'transparent',
          textAlign: 'center',
        }}
      >
        <UploadIcon />
        {file ? (
          <div>
            <div
              style={{
                fontWeight: 600,
                color: 'var(--color-text)',
                fontSize: 'var(--text-body)',
                marginBottom: 'var(--space-1)',
              }}
            >
              {file.name}
            </div>
            <div style={{ color: 'var(--color-text-muted)', fontSize: 'var(--text-caption)' }}>
              {formatFileSize(file.size)} · Click to change
            </div>
          </div>
        ) : (
          <div>
            <div
              style={{
                fontWeight: 600,
                color: 'var(--color-text)',
                fontSize: 'var(--text-h4)',
                marginBottom: 'var(--space-1)',
              }}
            >
              Drop your spreadsheet here
            </div>
            <div style={{ color: 'var(--color-text-muted)', fontSize: 'var(--text-body)' }}>
              or{' '}
              <span style={{ color: 'var(--color-accent-primary)', fontWeight: 500 }}>
                click to browse
              </span>
            </div>
            <div
              style={{
                marginTop: 'var(--space-2)',
                color: 'var(--color-text-faint)',
                fontSize: 'var(--text-caption)',
              }}
            >
              Supports .xlsx, .xls, .csv
            </div>
          </div>
        )}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept=".xlsx,.xls,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv"
        aria-hidden="true"
        tabIndex={-1}
        style={{ display: 'none' }}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) handleFile(f);
        }}
      />

      {error && (
        <div
          role="alert"
          style={{
            marginTop: 'var(--space-4)',
            padding: 'var(--space-3) var(--space-4)',
            borderRadius: 'var(--radius-md)',
            background: 'var(--color-danger-subtle, #fee2e2)',
            color: 'var(--color-danger, #b91c1c)',
            border: '1px solid var(--color-danger, #b91c1c)',
            fontSize: 'var(--text-caption)',
          }}
        >
          {error}
        </div>
      )}

      <div style={{ marginTop: 'var(--space-4)', display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="primary"
          disabled={!file || analyzing}
          loading={analyzing}
          onClick={() => void handleAnalyze()}
          aria-label="Analyze uploaded file"
        >
          {analyzing ? 'Analyzing…' : 'Analyze'}
        </Button>
      </div>
    </div>
  );
}

// ─── Step 2: Mapping Preview ──────────────────────────────────────────────────

function MappingStep({
  analysis,
  file,
  onImported,
  onBack,
}: {
  analysis: AnalyzeResponse;
  file: File;
  onImported: (result: ExecuteResponse) => void;
  onBack: () => void;
}) {
  const { data: lots, loading: lotsLoading } = useApi<CellLine[]>('/api/v1/lots');

  const [mappings, setMappings] = useState<ColumnMapping[]>(analysis.mappings);
  const [selectedLotId, setSelectedLotId] = useState('');
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const updateMapping = (colIdx: number, newTarget: string) => {
    setMappings((prev) =>
      prev.map((m) =>
        m.column_index === colIdx
          ? { ...m, mapped_to: newTarget, confidence: 1.0, notes: 'Manually set' }
          : m,
      ),
    );
  };

  const handleImport = async () => {
    if (!selectedLotId) {
      setError('Please select a lot to import into.');
      return;
    }

    setImporting(true);
    setError(null);

    try {
      // Build entries from sample_rows (all rows from analysis)
      // We need to re-read from the original file for full data
      const form = new FormData();
      form.append('file', file);

      const TOKEN_KEY = 'lineageatlas_token';
      const token = localStorage.getItem(TOKEN_KEY);
      const headers: Record<string, string> = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;

      // Re-parse the file fully to get all rows
      const reRes = await fetch('/api/v1/import/analyze', {
        method: 'POST',
        headers,
        body: form,
      });

      if (!reRes.ok) {
        throw new Error('Failed to re-read file for import');
      }

      const reJson = (await reRes.json()) as { data: AnalyzeResponse };
      const allRows = reJson.data.sample_rows; // up to 20 rows from parser

      // Build entries from rows
      const activeMarkerMappings = mappings.filter((m) => m.mapped_to !== '__skip__');

      const entries = allRows
        .filter((row) => row.some((cell) => cell !== '' && cell != null))
        .map((row) => {
          const markers = activeMarkerMappings
            .map((m) => {
              const raw = row[m.column_index];
              const value =
                typeof raw === 'number' ? raw : parseFloat(String(raw).replace(/[^0-9.-]/g, ''));
              if (isNaN(value)) return null;
              return { name: m.mapped_to, value };
            })
            .filter((m): m is { name: string; value: number } => m !== null);

          // Extract date
          let date: string | undefined;
          if (analysis.date_column != null) {
            const rawDate = row[analysis.date_column];
            if (rawDate) {
              const parsed = new Date(String(rawDate));
              if (!isNaN(parsed.getTime())) {
                date = parsed.toISOString();
              }
            }
          }

          // Extract passage
          let passage: number | undefined;
          if (analysis.passage_column != null) {
            const rawPass = row[analysis.passage_column];
            const passNum = parseInt(String(rawPass).replace(/[^0-9]/g, ''), 10);
            if (!isNaN(passNum)) passage = passNum;
          }

          return { markers, date, passage };
        })
        .filter((e) => e.markers.length > 0);

      const result = await apiFetch<ExecuteResponse>('/api/v1/import/execute', {
        method: 'POST',
        body: JSON.stringify({ lot_id: selectedLotId, entries }),
      });

      onImported(result.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Import failed. Please try again.');
    } finally {
      setImporting(false);
    }
  };

  const colLabelStyle: React.CSSProperties = {
    fontSize: 'var(--text-caption)',
    fontWeight: 600,
    color: 'var(--color-text-muted)',
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    padding: 'var(--space-2) var(--space-4)',
    background: 'var(--color-bg-subtle, rgba(0,0,0,0.02))',
    borderBottom: '2px solid var(--color-border)',
    textAlign: 'left' as const,
  };

  return (
    <div>
      {/* Warnings */}
      {analysis.warnings.length > 0 && (
        <div
          style={{
            marginBottom: 'var(--space-4)',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--space-2)',
          }}
        >
          {analysis.warnings.map((w, i) => (
            <div
              key={i}
              role="status"
              style={{
                padding: 'var(--space-3) var(--space-4)',
                borderRadius: 'var(--radius-md)',
                background: 'var(--color-warning-subtle, #fef3c7)',
                color: 'var(--color-warning-text, #92400e)',
                border: '1px solid var(--color-warning, #d97706)',
                fontSize: 'var(--text-caption)',
              }}
            >
              {w}
            </div>
          ))}
        </div>
      )}

      {/* Summary stats */}
      <div
        style={{
          display: 'flex',
          gap: 'var(--space-4)',
          marginBottom: 'var(--space-6)',
          flexWrap: 'wrap',
        }}
      >
        {[
          { label: 'Columns', value: analysis.headers.length },
          { label: 'Mapped markers', value: mappings.length },
          { label: 'Data rows', value: analysis.total_rows },
        ].map((stat) => (
          <div
            key={stat.label}
            style={{
              padding: 'var(--space-3) var(--space-5)',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--color-border)',
              background: 'var(--color-bg-elevated)',
              flex: '1 1 100px',
              textAlign: 'center',
            }}
          >
            <div
              style={{
                fontSize: 'var(--text-h3)',
                fontWeight: 700,
                color: 'var(--color-accent-primary)',
                fontFamily: 'var(--font-display)',
              }}
            >
              {stat.value}
            </div>
            <div
              style={{
                fontSize: 'var(--text-xs)',
                color: 'var(--color-text-muted)',
                marginTop: '2px',
              }}
            >
              {stat.label}
            </div>
          </div>
        ))}
      </div>

      {/* Column mapping table */}
      <Card>
        <h3
          style={{
            fontFamily: 'var(--font-display)',
            fontSize: 'var(--text-h4)',
            fontWeight: 600,
            color: 'var(--color-text)',
            margin: '0 0 var(--space-4)',
          }}
        >
          Column Mappings
        </h3>
        <div style={{ overflowX: 'auto', margin: '0 calc(-1 * var(--space-4))' }}>
          <table
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontFamily: 'var(--font-body)',
              fontSize: 'var(--text-body)',
            }}
            aria-label="Column mappings table"
          >
            <thead>
              <tr>
                <th scope="col" style={colLabelStyle}>
                  Column Header
                </th>
                <th scope="col" style={colLabelStyle}>
                  Mapped To
                </th>
                <th scope="col" style={colLabelStyle}>
                  Confidence
                </th>
                <th scope="col" style={colLabelStyle}>
                  Notes
                </th>
              </tr>
            </thead>
            <tbody>
              {mappings.map((m) => (
                <tr key={m.column_index} style={{ borderBottom: '1px solid var(--color-border)' }}>
                  <td
                    style={{
                      padding: 'var(--space-3) var(--space-4)',
                      color: 'var(--color-text)',
                      fontWeight: 500,
                    }}
                  >
                    {m.column_header}
                  </td>
                  <td style={{ padding: 'var(--space-3) var(--space-4)' }}>
                    <select
                      value={m.mapped_to}
                      onChange={(e) => updateMapping(m.column_index, e.target.value)}
                      aria-label={`Mapping for column ${m.column_header}`}
                      style={SELECT_STYLE}
                    >
                      <option value="__skip__">— Skip —</option>
                      {KNOWN_MARKERS.filter((mk) => mk !== '__skip__').map((mk) => (
                        <option key={mk} value={mk}>
                          {mk}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td style={{ padding: 'var(--space-3) var(--space-4)' }}>
                    <Badge status={confidenceVariant(m.confidence)}>
                      {confidenceLabel(m.confidence)}
                    </Badge>
                  </td>
                  <td
                    style={{
                      padding: 'var(--space-3) var(--space-4)',
                      color: 'var(--color-text-muted)',
                      fontSize: 'var(--text-caption)',
                    }}
                  >
                    {m.notes}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Detected metadata columns */}
      {(analysis.lot_column != null ||
        analysis.date_column != null ||
        analysis.passage_column != null) && (
        <div
          style={{
            marginTop: 'var(--space-4)',
            padding: 'var(--space-3) var(--space-4)',
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--color-border)',
            background: 'var(--color-bg-elevated)',
            fontSize: 'var(--text-caption)',
            color: 'var(--color-text-muted)',
            display: 'flex',
            gap: 'var(--space-4)',
            flexWrap: 'wrap',
          }}
        >
          {analysis.lot_column != null && (
            <span>
              Lot column:{' '}
              <strong style={{ color: 'var(--color-text)' }}>
                {analysis.headers[analysis.lot_column]}
              </strong>
            </span>
          )}
          {analysis.date_column != null && (
            <span>
              Date column:{' '}
              <strong style={{ color: 'var(--color-text)' }}>
                {analysis.headers[analysis.date_column]}
              </strong>
            </span>
          )}
          {analysis.passage_column != null && (
            <span>
              Passage column:{' '}
              <strong style={{ color: 'var(--color-text)' }}>
                {analysis.headers[analysis.passage_column]}
              </strong>
            </span>
          )}
        </div>
      )}

      {/* Lot selector */}
      <div style={{ marginTop: 'var(--space-6)' }}>
        <label
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--space-1)',
            maxWidth: '360px',
          }}
        >
          <span
            style={{
              fontSize: 'var(--text-xs)',
              color: 'var(--color-text-muted)',
              fontWeight: 500,
              letterSpacing: '0.03em',
              textTransform: 'uppercase',
            }}
          >
            Import into Lot *
          </span>
          {lotsLoading ? (
            <div
              style={{
                height: '36px',
                borderRadius: 'var(--radius-md)',
                background: 'var(--color-bg-elevated)',
                animation: 'pulse 1.5s ease-in-out infinite',
              }}
            />
          ) : (
            <select
              value={selectedLotId}
              onChange={(e) => {
                setSelectedLotId(e.target.value);
                setError(null);
              }}
              aria-label="Select lot to import into"
              style={{
                ...SELECT_STYLE,
                height: '36px',
                fontSize: 'var(--text-body)',
                color: selectedLotId ? 'var(--color-text)' : 'var(--color-text-muted)',
              }}
            >
              <option value="">Select a lot…</option>
              {(lots ?? []).map((lot) => (
                <option key={lot.id} value={lot.id}>
                  {lot.lot_number} — {lot.cell_type}
                  {lot.status ? ` (${lot.status})` : ''}
                </option>
              ))}
            </select>
          )}
        </label>
      </div>

      {error && (
        <div
          role="alert"
          style={{
            marginTop: 'var(--space-4)',
            padding: 'var(--space-3) var(--space-4)',
            borderRadius: 'var(--radius-md)',
            background: 'var(--color-danger-subtle, #fee2e2)',
            color: 'var(--color-danger, #b91c1c)',
            border: '1px solid var(--color-danger, #b91c1c)',
            fontSize: 'var(--text-caption)',
          }}
        >
          {error}
        </div>
      )}

      <div
        style={{
          marginTop: 'var(--space-6)',
          display: 'flex',
          gap: 'var(--space-3)',
          justifyContent: 'flex-end',
        }}
      >
        <Button variant="quiet" onClick={onBack} disabled={importing}>
          Back
        </Button>
        <Button
          variant="primary"
          disabled={!selectedLotId || importing}
          loading={importing}
          onClick={() => void handleImport()}
          aria-label="Import entries"
        >
          {importing ? 'Importing…' : 'Import'}
        </Button>
      </div>
    </div>
  );
}

// ─── Step 3: Results ──────────────────────────────────────────────────────────

function ResultsStep({
  result,
  onImportAnother,
}: {
  result: ExecuteResponse;
  onImportAnother: () => void;
}) {
  const navigate = useNavigate();

  return (
    <div>
      {/* Success banner */}
      <div
        role="status"
        aria-live="polite"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-4)',
          padding: 'var(--space-5) var(--space-6)',
          borderRadius: 'var(--radius-lg)',
          background: 'var(--color-success-subtle, #dcfce7)',
          border: '1px solid var(--color-success, #15803d)',
          marginBottom: 'var(--space-6)',
        }}
      >
        <svg width="32" height="32" viewBox="0 0 32 32" fill="none" aria-hidden="true">
          <circle cx="16" cy="16" r="15" stroke="var(--color-success, #15803d)" strokeWidth="1.5" />
          <path
            d="M10 16l4 4 8-8"
            stroke="var(--color-success, #15803d)"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        <div>
          <div
            style={{
              fontWeight: 600,
              color: 'var(--color-success, #15803d)',
              fontSize: 'var(--text-h4)',
              fontFamily: 'var(--font-display)',
            }}
          >
            Import complete
          </div>
          <div style={{ color: 'var(--color-success, #15803d)', fontSize: 'var(--text-body)' }}>
            {result.imported_count} QC{result.imported_count === 1 ? ' entry' : ' entries'} imported
            successfully.
          </div>
        </div>
      </div>

      {/* Entry list */}
      <Card>
        <h3
          style={{
            fontFamily: 'var(--font-display)',
            fontSize: 'var(--text-h4)',
            fontWeight: 600,
            color: 'var(--color-text)',
            margin: '0 0 var(--space-4)',
          }}
        >
          Imported Entries
        </h3>
        <div
          style={{
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--color-border)',
            overflow: 'hidden',
          }}
        >
          <table
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontFamily: 'var(--font-body)',
              fontSize: 'var(--text-body)',
            }}
            aria-label="Imported entries"
          >
            <thead>
              <tr
                style={{
                  background: 'var(--color-bg-subtle, rgba(0,0,0,0.02))',
                  borderBottom: '2px solid var(--color-border)',
                }}
              >
                {['Entry ID', 'Overall Result'].map((h) => (
                  <th
                    key={h}
                    scope="col"
                    style={{
                      padding: 'var(--space-2) var(--space-4)',
                      textAlign: 'left',
                      fontSize: 'var(--text-caption)',
                      fontWeight: 600,
                      color: 'var(--color-text-muted)',
                      letterSpacing: '0.04em',
                      textTransform: 'uppercase',
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {result.entries.map((entry) => (
                <tr key={entry.id} style={{ borderBottom: '1px solid var(--color-border)' }}>
                  <td
                    style={{
                      padding: 'var(--space-3) var(--space-4)',
                      fontFamily: 'var(--font-mono, monospace)',
                      fontSize: 'var(--text-caption)',
                      color: 'var(--color-text-muted)',
                    }}
                  >
                    {entry.id}
                  </td>
                  <td style={{ padding: 'var(--space-3) var(--space-4)' }}>
                    <Badge status={resultVariant(entry.overall_result)}>
                      {entry.overall_result.charAt(0).toUpperCase() + entry.overall_result.slice(1)}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <div
        style={{
          marginTop: 'var(--space-6)',
          display: 'flex',
          gap: 'var(--space-3)',
          flexWrap: 'wrap',
        }}
      >
        <Button
          variant="primary"
          onClick={() => navigate(`/lots/${result.lot_id}`)}
          aria-label="View lot detail"
        >
          View Lot Detail
        </Button>
        <Button variant="quiet" onClick={onImportAnother} aria-label="Import another file">
          Import Another
        </Button>
      </div>
    </div>
  );
}

// ─── Step indicator ───────────────────────────────────────────────────────────

const STEPS: { id: Step; label: string }[] = [
  { id: 'upload', label: 'Upload' },
  { id: 'mapping', label: 'AI Mapping' },
  { id: 'results', label: 'Results' },
];

function StepIndicator({ current }: { current: Step }) {
  const currentIdx = STEPS.findIndex((s) => s.id === current);
  return (
    <div
      role="progressbar"
      aria-label="Import wizard progress"
      aria-valuenow={currentIdx + 1}
      aria-valuemin={1}
      aria-valuemax={STEPS.length}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 0,
        marginBottom: 'var(--space-8)',
      }}
    >
      {STEPS.map((step, i) => {
        const isActive = step.id === current;
        const isDone = i < currentIdx;
        return (
          <div
            key={step.id}
            style={{ display: 'flex', alignItems: 'center', flex: i < STEPS.length - 1 ? 1 : 0 }}
          >
            <div
              style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}
            >
              <div
                aria-current={isActive ? 'step' : undefined}
                style={{
                  width: '28px',
                  height: '28px',
                  borderRadius: 'var(--radius-full)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '12px',
                  fontWeight: 700,
                  background: isActive
                    ? 'var(--color-accent-primary)'
                    : isDone
                      ? 'var(--color-success, #15803d)'
                      : 'var(--color-bg-elevated)',
                  color: isActive || isDone ? '#0a0a0b' : 'var(--color-text-muted)',
                  border: `2px solid ${isActive ? 'var(--color-accent-primary)' : isDone ? 'var(--color-success, #15803d)' : 'var(--color-border)'}`,
                  transition: 'all var(--duration-normal)',
                }}
              >
                {isDone ? '✓' : i + 1}
              </div>
              <span
                style={{
                  fontSize: 'var(--text-xs)',
                  color: isActive ? 'var(--color-text)' : 'var(--color-text-muted)',
                  fontWeight: isActive ? 600 : 400,
                  whiteSpace: 'nowrap',
                }}
              >
                {step.label}
              </span>
            </div>
            {i < STEPS.length - 1 && (
              <div
                aria-hidden="true"
                style={{
                  flex: 1,
                  height: '2px',
                  background: isDone ? 'var(--color-success, #15803d)' : 'var(--color-border)',
                  marginBottom: '20px',
                  marginLeft: '4px',
                  marginRight: '4px',
                  transition: 'background var(--duration-normal)',
                }}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── ImportPage ───────────────────────────────────────────────────────────────

export function ImportPage() {
  const [step, setStep] = useState<Step>('upload');
  const [analysis, setAnalysis] = useState<AnalyzeResponse | null>(null);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [importResult, setImportResult] = useState<ExecuteResponse | null>(null);

  const handleAnalyzed = (result: AnalyzeResponse, file: File) => {
    setAnalysis(result);
    setUploadedFile(file);
    setStep('mapping');
  };

  const handleImported = (result: ExecuteResponse) => {
    setImportResult(result);
    setStep('results');
  };

  const handleReset = () => {
    setStep('upload');
    setAnalysis(null);
    setUploadedFile(null);
    setImportResult(null);
  };

  return (
    <div>
      {/* Page header */}
      <div style={{ marginBottom: 'var(--space-8)' }}>
        <h1
          style={{
            fontFamily: 'var(--font-display)',
            fontSize: 'var(--text-h1)',
            fontWeight: 600,
            letterSpacing: 'var(--tracking-heading)',
            lineHeight: 'var(--leading-heading)',
            color: 'var(--color-text)',
            margin: '0 0 var(--space-2)',
          }}
        >
          Import QC Data
        </h1>
        <p style={{ color: 'var(--color-text-muted)', fontSize: 'var(--text-body)', margin: 0 }}>
          Upload a spreadsheet and Claude AI will map your columns to UC-MSC QC markers
          automatically.
        </p>
      </div>

      <StepIndicator current={step} />

      <Card>
        {step === 'upload' && <UploadStep onAnalyzed={handleAnalyzed} />}

        {step === 'mapping' && analysis && uploadedFile && (
          <MappingStep
            analysis={analysis}
            file={uploadedFile}
            onImported={handleImported}
            onBack={handleReset}
          />
        )}

        {step === 'results' && importResult && (
          <ResultsStep result={importResult} onImportAnother={handleReset} />
        )}
      </Card>
    </div>
  );
}
